import React from "react";
import {Box, Button, ButtonBase, Divider, IconButton, List, ListItemButton, ListItemText, Stack, Typography} from "@mui/material";
import {ChevronLeft, ChevronRight} from "@mui/icons-material";
import dayjs from "dayjs";
import type {Month, MonthRange} from "./monthRange";
import {formatMonthRange, inMonthRange, monthName, monthOf, monthRangePresets, sameMonthRange} from "./monthRange";

type PickerProps = {
    value: MonthRange | null
    defaultRange: MonthRange | null
    onOk: (range: MonthRange | null) => void
    onCancel: () => void
}

/**
 * Presets on the left, a month grid on the right: click the first and the last month, then OK.
 */
export const MonthRangePicker: React.FC<PickerProps> = ({value, defaultRange, onOk, onCancel}) => {
    const today = dayjs();
    // The range being picked: [first click, second click]
    const [draft, setDraft] = React.useState<[Month, Month | null] | null>(value);
    const [hover, setHover] = React.useState<Month | null>(null);
    const [year, setYear] = React.useState(Number((value?.[1] ?? today.format('YYYY-MM')).slice(0, 4)));
    const presets = monthRangePresets(today, defaultRange, year);
    const picking = draft !== null && draft[1] === null;
    const preview: [Month, Month | null] | null = picking && hover ? [draft[0], hover] : draft;
    const complete: MonthRange | null = draft && draft[1] ? (draft[0] <= draft[1] ? [draft[0], draft[1]] : [draft[1], draft[0]]) : null;

    const pick = (m: Month) => setDraft(picking ? [draft[0], m] : [m, null]);

    return <Stack direction="row" className="month-range-picker">
        <List dense sx={{width: '13em', py: 1}}>
            {presets.map(preset => <ListItemButton
                key={preset.label}
                selected={sameMonthRange(preset.range, complete)}
                onClick={() => onOk(preset.range)}>
                <ListItemText primary={preset.label}/>
            </ListItemButton>)}
        </List>
        <Divider orientation="vertical" flexItem/>
        <Box sx={{p: 2, width: '20em'}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <IconButton size="small" aria-label="Previous year" onClick={() => setYear(year - 1)}><ChevronLeft/></IconButton>
                <Typography variant="subtitle1" fontWeight={600}>{year}</Typography>
                <IconButton size="small" aria-label="Next year" onClick={() => setYear(year + 1)}><ChevronRight/></IconButton>
            </Stack>
            <Box className="month-range-grid" onMouseLeave={() => setHover(null)}>
                {Array.from({length: 12}, (_, i) => monthOf(year, i)).map(m => {
                    const isEdge = Boolean(preview && (m === preview[0] || m === preview[1]));
                    return <ButtonBase
                        key={m}
                        className={'month-range-cell' + (inMonthRange(m, preview) ? ' in-range' : '') + (isEdge ? ' edge' : '')
                            + (m === today.format('YYYY-MM') ? ' current' : '')}
                        aria-pressed={inMonthRange(m, preview)}
                        onMouseEnter={() => setHover(m)}
                        onClick={() => pick(m)}>
                        {monthName(m)}
                    </ButtonBase>;
                })}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{mt: 1.5, minHeight: '1.5em'}}>
                {picking ? `From ${formatMonthRange([draft[0], draft[0]])}: click the last month`
                    : complete ? formatMonthRange(complete) : 'Click the first month'}
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={1} sx={{mt: 1}}>
                <Button size="small" disabled={!draft} onClick={() => setDraft(null)}>Clear</Button>
                <Button size="small" onClick={onCancel}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={picking}
                        onClick={() => onOk(complete)}>
                    OK
                </Button>
            </Stack>
        </Box>
    </Stack>;
};
